import type { Persona, PrototypeBootstrap, PrototypeCommand } from "@peer-start/platform-contracts";
import { Sparkles } from "lucide-react";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { EmployeeProductTourHost } from "../../domains/passport/components/EmployeeProductTourHost.js";
import { isMentor, workspaceHome } from "../../domains/prototype/data/user-management.js";
import { AppSidebar } from "./AppSidebar.js";
import { AppTopbar } from "./AppTopbar.js";
import { MobileNavigation } from "./MobileNavigation.js";
import { useSidebarExpanded } from "./sidebar-state.js";

interface AppShellProps {
  availablePersonas: readonly Persona[];
  bootstrap: PrototypeBootstrap;
  persona: Persona;
  onPersonaChange: (persona: Persona) => Promise<void>;
  runCommand: (command: PrototypeCommand) => Promise<string>;
  commandPending: boolean;
}

export function AppShell({
  availablePersonas,
  bootstrap,
  persona,
  onPersonaChange,
  runCommand,
  commandPending,
}: AppShellProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const isChatPage = location.pathname === "/me/chat" || location.pathname === "/manage/chat";
  const isSchoolSetupPage = location.pathname === "/manage/school/setup";
  const isCardWorkspace = /^\/admin\/situation-cards\/[^/]+$/.test(location.pathname);
  const main = useRef<HTMLElement>(null);
  useEffect(() => {
    if (main.current && !location.hash) main.current.scrollTop = 0;
  }, [location.pathname, location.hash]);
  const workspace =
    persona.kind === "employee" ? "employee" : location.pathname.startsWith("/admin") ? "admin" : "manager";
  const sidebar = useSidebarExpanded(location.pathname, workspace);

  const changePersona = async (id: string) => {
    const next = availablePersonas.find((entry) => entry.id === id);
    if (!next) return;
    await onPersonaChange(next);
    navigate(workspaceHome(next));
  };

  return (
    <div className="flex h-dvh overflow-hidden bg-background text-foreground">
      {!isSchoolSetupPage ? (
        <AppSidebar
          bootstrap={bootstrap}
          expanded={sidebar.expanded}
          onPersonaChange={(id) => void changePersona(id)}
          pathname={location.pathname}
          persona={persona}
          workspace={workspace}
        />
      ) : null}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        {isSchoolSetupPage ? (
          <header className="flex h-16 shrink-0 items-center gap-3 border-b border-border bg-background px-5 sm:px-8">
            <span className="grid size-10 place-items-center rounded-xl bg-primary text-primary-foreground">
              <Sparkles aria-hidden="true" className="size-4" />
            </span>
            <div>
              <p className="font-medium">{t("common.appName")}</p>
              <p className="text-xs text-muted-foreground">{bootstrap.organisation.name}</p>
            </div>
          </header>
        ) : (
          <AppTopbar
            bootstrap={bootstrap}
            sidebarExpanded={sidebar.expanded}
            onPersonaChange={(id) => void changePersona(id)}
            onSidebarToggle={sidebar.toggle}
            pathname={location.pathname}
            persona={persona}
            search={location.search}
            workspace={workspace}
          />
        )}
        <main
          ref={main}
          className={
            isSchoolSetupPage
              ? "min-w-0 flex-1 overflow-y-auto px-5 py-6 sm:px-8"
              : isCardWorkspace
                ? "flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
                : `min-w-0 flex-1 overflow-y-auto px-4 pb-28 sm:px-6 md:pb-8 lg:px-10 xl:px-12 ${
                    isChatPage ? "relative pt-0" : "py-6 lg:py-10"
                  }`
          }
        >
          <div
            className={isCardWorkspace ? "flex min-h-0 flex-1 flex-col" : "relative mx-auto max-w-[1500px]"}
          >
            <Outlet
              context={{
                bootstrap,
                persona,
                runCommand,
                commandPending,
                selectPersona: changePersona,
              }}
            />
          </div>
        </main>
      </div>

      {!isSchoolSetupPage ? <MobileNavigation mentor={isMentor(persona)} workspace={workspace} /> : null}

      {persona.kind === "employee" ? (
        <EmployeeProductTourHost bootstrap={bootstrap} persona={persona} />
      ) : null}
    </div>
  );
}
